<template>
  <!-- Пре-шаг демо/поддержки + проектный триггер + меню — только главная/desktop -->
  <template v-if="isMainGate">
    <div class="widget-wrap" v-on-click-outside="onClickOutside">
      <!-- Внутри .widget-wrap, иначе клик по пункту считается «кликом снаружи» и меню размонтируется раньше emit -->
      <ChatMainStage
          v-if="isShowStage"
          @demo="handleDemo"
          @support="handleSupport"
          @dismiss="isShowStage = false"
      />
      <div class="contact-btn" @click="toggleStage" :data-open="isShowPopover || isShowStage">
        <svg v-if="!isShowPopover && !isShowStage && !isChatOpen" viewBox="0 0 33 33" fill="none"
             xmlns="http://www.w3.org/2000/svg" class="icon">
          <path
              d="M14.437 12.3781C14.6793 11.6894 15.1574 11.1086 15.7869 10.7387C16.4163 10.3688 17.1563 10.2336 17.8759 10.357C18.5954 10.4804 19.2481 10.8545 19.7183 11.4131C20.1885 11.9716 20.4458 12.6785 20.4447 13.4086C20.4447 15.4695 17.3532 16.5 17.3532 16.5M17.3931 20.625H17.4069M17.187 27.5C23.6418 27.5 28.8745 22.2673 28.8745 15.8125C28.8745 9.35767 23.6418 4.125 17.187 4.125C10.7322 4.125 5.49948 9.35767 5.49948 15.8125C5.49948 17.1187 5.71377 18.3749 6.10912 19.5478C6.25789 19.9892 6.33228 20.2099 6.3457 20.3795C6.35895 20.5469 6.34893 20.6643 6.30751 20.8271C6.26556 20.9919 6.17295 21.1633 5.98774 21.5061L3.73871 25.669C3.41791 26.2628 3.2575 26.5597 3.2934 26.7888C3.32467 26.9884 3.44214 27.1641 3.6146 27.2693C3.8126 27.3901 4.14827 27.3554 4.8196 27.286L11.861 26.5581C12.0742 26.5361 12.1809 26.5251 12.278 26.5288C12.3736 26.5325 12.4411 26.5414 12.5343 26.5629C12.6291 26.5848 12.7482 26.6307 12.9866 26.7225C14.2901 27.2247 15.7064 27.5 17.187 27.5Z"
              stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <svg v-else viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" class="icon">
          <path d="M26.2067 26.2067L8.5 8.5" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round"/>
          <path d="M26.2067 8.5L8.5 26.2067" stroke="currentColor" stroke-width="2" stroke-linecap="round"
                stroke-linejoin="round"/>
        </svg>
      </div>
      <div v-if="isShowPopover" class="popover">
        <SupportPanel @chat="isShowPopover = false"/>
      </div>
    </div>
    <AskDemo ref="askDemoRef"/>
  </template>

  <!-- Библиотечный виджет: на главной/desktop его кнопка и меню скрыты (CSS), но QChat активен через стор.
       Оборачиваем в div, т.к. библиотечный компонент рендерит фрагмент и не принимает fallthrough class. -->
  <div :class="{ 'cw-hide-trigger': isMainGate }">
    <LibraryChatWidget
        :company-uin="selectedContextCompany?.bin"
        tab-title="Docs"
    />
  </div>
</template>

<script setup lang="ts">
import {computed, ref, useTemplateRef} from 'vue';
import {useRoute} from 'vue-router';
import {storeToRefs} from 'pinia';
import {breakpointsVuetifyV3, useBreakpoints} from '@vueuse/core';
import {vOnClickOutside} from '@vueuse/components';
import {ChatWidget as LibraryChatWidget} from '@ismet/vue-ui-kit';

import {UserModel} from '@/entities/User';

import {AskDemo} from '@/features/AskDemo';
import {SupportPanel} from '@/features/SupportContacts';

import {useSupportChatStore} from '@/shared/model/supportChat.store';

import ChatMainStage from './ChatMainStage.vue';

const route = useRoute();
const isXs = useBreakpoints(breakpointsVuetifyV3).smaller('sm');

const user = UserModel.useUserStore();
const supportChatStore = useSupportChatStore();

const {selectedContextCompany} = storeToRefs(user);
const {isChatOpen} = storeToRefs(supportChatStore);

const isShowStage = ref<boolean>(false);
const isShowPopover = ref<boolean>(false);

const askDemoRef = useTemplateRef<InstanceType<typeof AskDemo>>('askDemoRef');

const isMainGate = computed(() => route.name === 'main' && !isXs.value);

const onClickOutside = () => {
  isShowStage.value = false;
  isShowPopover.value = false;
};

const toggleStage = () => {
  if (isChatOpen.value) {
    supportChatStore.closeChat();
    return;
  }
  if (isShowPopover.value) {
    isShowPopover.value = false;
    return;
  }
  isShowStage.value = !isShowStage.value;
};

const handleDemo = () => {
  isShowStage.value = false;
  askDemoRef.value?.open();
};

const handleSupport = () => {
  isShowStage.value = false;
  isShowPopover.value = true;
};
</script>

<style scoped>
.widget-wrap {
  position: relative;
}

.contact-btn {
  position: fixed;
  bottom: 40px;
  right: 40px;
  z-index: 1000;
  height: 64px;
  width: 64px;
  background: var(--BW-blue-4);
  color: var(--on-color);
  border-radius: 100px;
  padding: 1rem;
  cursor: pointer;

  svg {
    width: 2rem;
    height: 2rem;
  }
}

.popover {
  position: fixed;
  bottom: 118px;
  right: 2rem;
  background: var(--surface);
  border-radius: 1rem;
  box-shadow: var(--shadow-popover);
  z-index: 1001;
  width: 100%;
  max-width: 350px;
  padding: 1.5rem 1rem;
  overflow: hidden;
}

/* На главной/desktop скрыть кнопку и меню библиотеки, оставив QChat (.popover.q-chat) */
.cw-hide-trigger :deep(.contact-btn) {
  display: none;
}

.cw-hide-trigger :deep(.popover:not(.q-chat)) {
  display: none;
}

/* Вернуть подъём кнопки библиотеки над мобильными панелями (библиотека на ≤680 ставит 4.75rem без переменной) */
@media (max-width: 680px) {
  :deep(.contact-btn) {
    bottom: calc(4.75rem + var(--chat-widget-bottom));
  }
}

@media (max-width: 1280px) {
  .contact-btn {
    bottom: calc(4.75rem + var(--chat-widget-bottom));
    right: 1.25rem;
    width: 2.5rem;
    height: 2.5rem;
    padding: 0;
    display: flex;
    justify-content: center;
    align-items: center;

    svg {
      height: 1.75rem;
      width: 1.75rem;
    }
  }

  .popover {
    bottom: 8rem;
    right: 0;
  }
}
</style>
